import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/apiClient";
import PipelineStepper from "@/components/PipelineStepper";
import { RequestTypeBadge, StageBadge } from "@/components/Badges";
import { REQUEST_TYPES, TEAMS } from "@/lib/crmConstants";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, MessageSquare, Activity, User, Phone, Mail, MapPin, ShieldCheck, Store, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function RecordDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [record, setRecord] = useState(null);
  const [pipelines, setPipelines] = useState({});
  const [activity, setActivity] = useState([]);
  const [waLogs, setWaLogs] = useState([]);
  const [nextStage, setNextStage] = useState("");
  const [reassignTeam, setReassignTeam] = useState("");
  const [reassignUserId, setReassignUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [r, p, a, w] = await Promise.all([
      api.get(`/records/${id}`),
      api.get(`/meta/pipelines`),
      api.get(`/records/${id}/activity`),
      api.get(`/records/${id}/whatsapp`),
    ]);
    setRecord(r.data);
    setPipelines(p.data.pipelines);
    setActivity(a.data);
    setWaLogs(w.data);
    setNextStage(r.data.stage);
    setReassignTeam(r.data.assigned_team || "");
    setReassignUserId(r.data.assigned_user_id || "");
  }, [id]);

  useEffect(() => {
    load().catch(() => nav("/records"));
    // Only admin / team_lead need the users list for reassignment; team_members can skip.
    if (user?.role === "admin" || user?.role === "team_lead") {
      api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
    }
  }, [load, nav, user?.role]);

  if (!record) return <div className="p-8 text-[#71717A]">Loading…</div>;

  const stages = pipelines[record.request_type] || [];
  const d = record.data || {};

  const changeStage = async () => {
    if (nextStage === record.stage) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/records/${id}/stage`, { stage: nextStage });
      setRecord(data);
      toast.success(`Stage → ${nextStage}`);
      const [a, w] = await Promise.all([
        api.get(`/records/${id}/activity`),
        api.get(`/records/${id}/whatsapp`),
      ]);
      setActivity(a.data);
      setWaLogs(w.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change stage");
    } finally {
      setBusy(false);
    }
  };

  const reassign = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (reassignTeam && reassignTeam !== record.assigned_team) payload.team = reassignTeam;
      if (reassignUserId !== (record.assigned_user_id || "")) payload.assigned_user_id = reassignUserId;
      if (!Object.keys(payload).length) { setBusy(false); return; }
      const { data } = await api.patch(`/records/${id}/reassign`, payload);
      setRecord(data);
      toast.success("Record reassigned");
      const a = await api.get(`/records/${id}/activity`);
      setActivity(a.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reassign");
    } finally {
      setBusy(false);
    }
  };

  const canReassign = user?.role === "admin" || user?.role === "team_lead";
  const teamCandidates = users.filter((u) => (u.teams || []).includes(record?.assigned_team || reassignTeam));
  const assigneeName = users.find((u) => u.id === record?.assigned_user_id)?.name;

  return (
    <div className="p-8 space-y-6">
      <button
        onClick={() => nav(-1)}
        className="text-sm text-[#71717A] hover:text-[#0A0A0A] flex items-center gap-1"
        data-testid="detail-back"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <RequestTypeBadge requestType={record.request_type} />
            <span className="data-label">Ticket</span>
            <span className="font-mono font-medium text-[15px]">{record.ticket_id}</span>
          </div>
          <h1 className="font-heading font-black text-3xl tracking-tight">
            {d.full_name || d.customer_name || d.contact_person || d.applicant_name || d.buyer_name || "Unnamed"}
          </h1>
          <div className="mt-2 text-sm text-[#52525B] flex flex-wrap gap-4">
            {d.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {d.phone}</span>}
            {d.email && <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {d.email}</span>}
            {(d.city || d.delivery_location) && (
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {d.city || d.delivery_location}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          <StageBadge stage={record.stage} requestType={record.request_type} />
          {user?.role === "admin" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" className="border-[#FCA5A5] text-[#DC2626] hover:bg-[#FEF2F2]" data-testid="detail-delete-button">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete record
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete {record.ticket_id}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently removes the record and its WhatsApp message logs.
                    An audit-log entry is preserved. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="detail-delete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                    onClick={async () => {
                      try {
                        await api.delete(`/records/${id}`);
                        toast.success(`${record.ticket_id} deleted`);
                        nav("/records");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Delete failed");
                      }
                    }}
                    data-testid="detail-delete-confirm"
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {/* Pipeline */}
      <div className="card-shell p-6">
        <div className="data-label mb-4">Pipeline</div>
        <PipelineStepper
          requestType={record.request_type}
          stages={stages}
          currentStage={record.stage}
          onStageClick={(s) => setNextStage(s)}
        />
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Select value={nextStage} onValueChange={setNextStage}>
            <SelectTrigger className="w-[260px]" data-testid="stage-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {stages.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
          <Button
            onClick={changeStage}
            disabled={busy || nextStage === record.stage}
            className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
            data-testid="apply-stage-button"
          >
            Apply stage change
          </Button>
          {canReassign && (
            <>
              <div className="h-6 w-px bg-[#E5E5E5]" />
              <span className="data-label">Assign to</span>
              <Select value={reassignUserId || "__none"} onValueChange={(v) => setReassignUserId(v === "__none" ? "" : v)}>
                <SelectTrigger className="w-[240px]" data-testid="reassign-user-select">
                  <SelectValue placeholder="Choose person" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— Unassigned —</SelectItem>
                  {teamCandidates.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {user?.role === "admin" && (
                <Select value={reassignTeam || undefined} onValueChange={setReassignTeam}>
                  <SelectTrigger className="w-[180px]" data-testid="reassign-team-select">
                    <SelectValue placeholder="Team" />
                  </SelectTrigger>
                  <SelectContent>
                    {TEAMS.map((t) => (<SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" onClick={reassign} disabled={busy} data-testid="apply-reassign-button">
                Reassign
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: fields */}
        <div className="lg:col-span-2 card-shell p-6">
          <div className="data-label mb-4">Submission data</div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            {Object.entries(d).map(([k, v]) => {
              if (k === "shopify_verification") return null;
              return (
                <div key={k}>
                  <dt className="data-label">{k.replace(/_/g, " ")}</dt>
                  <dd className="text-sm mt-0.5 break-words">{typeof v === "object" ? JSON.stringify(v) : String(v || "—")}</dd>
                </div>
              );
            })}
          </dl>

          {record.request_type === "warranty" && record.possible_duplicate && record.duplicate_of && (
            <div
              className="mt-4 p-3 rounded-md border flex items-start gap-2 text-sm"
              style={{ backgroundColor: "#FFFBEB", borderColor: "#FDE68A", color: "#92400E" }}
              data-testid="duplicate-warning-banner"
            >
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded font-mono bg-[#FEF3C7] text-[#92400E]">Possible duplicate</span>
              <div className="flex-1">
                A record with the same customer phone was created on{" "}
                <span className="font-mono">{(record.duplicate_of.created_at || "").slice(0, 10)}</span>{" "}
                (ticket <a href={`/records/${record.duplicate_of.record_id}`} className="font-mono underline">{record.duplicate_of.ticket_id}</a>). Verify before approving the incentive.
              </div>
            </div>
          )}

          {record.request_type === "warranty" && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-4 rounded-md border" style={{
                backgroundColor: record.warranty_approval_status === "approved" ? "#F0FDF4" : record.warranty_approval_status === "rejected" ? "#FEF2F2" : "#FFFBEB",
                borderColor: record.warranty_approval_status === "approved" ? "#86EFAC" : record.warranty_approval_status === "rejected" ? "#FCA5A5" : "#FDE68A",
              }}>
                <div className="data-label mb-1">Warranty approval</div>
                <div className="font-heading font-bold text-lg capitalize">{record.warranty_approval_status || "pending"}</div>
                {record.warranty_rejection_reason && (
                  <div className="text-xs text-[#DC2626] mt-1">Reason: {record.warranty_rejection_reason}</div>
                )}
                {record.warranty_approval_by && (
                  <div className="text-xs text-[#71717A] mt-1">by {record.warranty_approval_by}</div>
                )}
              </div>
              {record.salesperson_user_id && (
                <div className="p-4 rounded-md border" style={{
                  backgroundColor: record.incentive_approval_status === "approved" ? "#F0FDF4" : record.incentive_approval_status === "rejected" ? "#FEF2F2" : "#FFFBEB",
                  borderColor: record.incentive_approval_status === "approved" ? "#86EFAC" : record.incentive_approval_status === "rejected" ? "#FCA5A5" : "#FDE68A",
                }}>
                  <div className="data-label mb-1">Sales incentive approval</div>
                  <div className="font-heading font-bold text-lg capitalize">{record.incentive_approval_status || "pending"}</div>
                  <div className="mt-1 text-xs">
                    Unique ID:{" "}
                    {record.unique_id_matched === true && (
                      <span className="px-1.5 py-0.5 rounded font-mono bg-[#DCFCE7] text-[#166534]">matched</span>
                    )}
                    {record.unique_id_matched === false && (
                      <span className="px-1.5 py-0.5 rounded font-mono bg-[#FEE2E2] text-[#991B1B]">unmatched</span>
                    )}
                  </div>
                  {record.incentive_rejection_reason && (
                    <div className="text-xs text-[#DC2626] mt-1">Reason: {record.incentive_rejection_reason}</div>
                  )}
                  {record.incentive_approval_by && (
                    <div className="text-xs text-[#71717A] mt-1">by {record.incentive_approval_by}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {d.shopify_verification && (
            <div className="mt-6 p-4 rounded-md border" style={{
              backgroundColor: d.shopify_verification.verified ? "#F0FDF4" : "#FEF9C3",
              borderColor: d.shopify_verification.verified ? "#86EFAC" : "#FDE047",
            }}>
              <div className="flex items-center gap-2 mb-1">
                <Store className="w-4 h-4" />
                <span className="data-label">Shopify verification</span>
              </div>
              {d.shopify_verification.verified ? (
                <div className="text-sm">
                  <ShieldCheck className="w-4 h-4 inline mr-1 text-green-700" />
                  Matched order <span className="font-mono">{d.shopify_verification.order.order_number}</span> —
                  {" "}{d.shopify_verification.order.product_title} · dealer {d.shopify_verification.order.dealer}
                </div>
              ) : (
                <div className="text-sm text-[#713F12]">No matching Shopify order was found. Manual verification recommended.</div>
              )}
            </div>
          )}

          {record.shopify_draft && (
            <div className="mt-4 p-4 rounded-md border" style={{ backgroundColor: "#F5F3FF", borderColor: "#C4B5FD" }}>
              <div className="flex items-center gap-2 mb-1">
                <Store className="w-4 h-4" />
                <span className="data-label">Shopify draft order</span>
              </div>
              <div className="text-sm font-mono">{record.shopify_draft.draft_order_id}</div>
              <div className="text-xs text-[#71717A] mt-1">Created {new Date(record.shopify_draft.created_at).toLocaleString()}</div>
            </div>
          )}
        </div>

        {/* Right: sidebar meta */}
        <aside className="card-shell p-6 space-y-4 h-fit">
          <div>
            <div className="data-label">Source</div>
            <div className="mt-1 text-sm">{record.source}</div>
          </div>
          <div>
            <div className="data-label">Assigned team</div>
            <div className="mt-1 text-sm">{record.assigned_team || "—"}</div>
          </div>
          <div>
            <div className="data-label">Assigned to</div>
            <div className="mt-1 text-sm" data-testid="assigned-user-display">
              {record.assigned_user_id
                ? (assigneeName || <span className="font-mono text-xs">{record.assigned_user_id.slice(0, 8)}…</span>)
                : <span className="text-[#A1A1AA]">Unassigned (shared queue)</span>}
            </div>
          </div>
          {record.state && (
            <div>
              <div className="data-label">State</div>
              <div className="mt-1 text-sm">{record.state}</div>
            </div>
          )}
          {(record.attachments || []).length > 0 && (
            <div>
              <div className="data-label">Attachments</div>
              <div className="mt-1 space-y-1">
                {record.attachments.map((a) => (
                  <a
                    key={a.id}
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.id}/download`}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-sm text-[#4F46E5] hover:underline truncate"
                    data-testid={`attachment-${a.id}`}
                  >
                    {a.filename}
                  </a>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="data-label">Created</div>
            <div className="mt-1 text-sm">{new Date(record.created_at).toLocaleString()}</div>
            {record.created_by_email && (
              <div className="text-xs text-[#71717A]">by {record.created_by_email}</div>
            )}
          </div>
          {record.resolved_at && (
            <div>
              <div className="data-label">Resolved</div>
              <div className="mt-1 text-sm">{new Date(record.resolved_at).toLocaleString()}</div>
            </div>
          )}
        </aside>
      </div>

      {/* Activity + WhatsApp */}
      <Tabs defaultValue="activity" className="card-shell p-6">
        <TabsList data-testid="record-tabs">
          <TabsTrigger value="activity" data-testid="tab-activity">
            <Activity className="w-4 h-4 mr-1" /> Activity ({activity.length})
          </TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">
            <MessageSquare className="w-4 h-4 mr-1" /> WhatsApp ({waLogs.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="activity" className="pt-4">
          <div className="space-y-3">
            {activity.map((a) => (
              <div key={a.id} className="flex items-start gap-3 pb-3 border-b border-[#EEEEEC] last:border-0" data-testid={`activity-item-${a.id}`}>
                <div className="w-8 h-8 rounded-full bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center shrink-0">
                  <User className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <div className="text-sm">
                    <span className="font-medium">{a.actor_email || "system"}</span>
                    <span className="text-[#71717A]"> · {a.action}</span>
                  </div>
                  {a.meta && Object.keys(a.meta).length > 0 && (
                    <div className="text-xs text-[#52525B] font-mono mt-0.5">{JSON.stringify(a.meta)}</div>
                  )}
                  <div className="text-xs text-[#A1A1AA] mt-0.5">{new Date(a.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
            {activity.length === 0 && <div className="text-sm text-[#71717A]">No activity yet.</div>}
          </div>
        </TabsContent>
        <TabsContent value="whatsapp" className="pt-4">
          <div className="space-y-3">
            {waLogs.map((w) => (
              <div key={w.id} className={`flex ${w.direction === "outbound" ? "justify-end" : "justify-start"}`} data-testid={`wa-msg-${w.id}`}>
                <div
                  className={`max-w-[70%] rounded-lg p-3 text-sm ${
                    w.direction === "outbound"
                      ? "bg-[#4F46E5] text-white"
                      : "bg-[#F4F4F5] text-[#0A0A0A]"
                  }`}
                >
                  <div>{w.body}</div>
                  <div className={`text-[10px] mt-1 font-mono ${w.direction === "outbound" ? "text-white/70" : "text-[#71717A]"}`}>
                    {w.direction === "outbound" ? "→" : "←"} {w.peer} · {new Date(w.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
            {waLogs.length === 0 && <div className="text-sm text-[#71717A]">No WhatsApp messages logged yet.</div>}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
